import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { ToggleCardBody } from './toggle-card-body';
import { ToggleCardFooter } from './toggle-card-footer';
import { ToggleCardHeader } from './toggle-card-header';
import { ToggleCardRoot } from './toggle-card-root';
import { ToggleCardRootContent } from './toggle-card-root-content';
import { ToggleCardRootTrigger } from './toggle-card-root-trigger';
import type { ToggleCardPropsInterface } from './types/toggle-card.types';

/**
 * Toggle cards expose an empty stretched native `<button>` (with
 * [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed)) so the card
 * surface stays clickable without putting header/body/footer content inside the
 * control name.
 *
 * Prefer a header for the accessible name (`aria-labelledby`). Pass `aria-label`
 * when there is no header, or to override the header-derived name.
 *
 * **Important:** it is critical that content of the card
 * does not change when its state changes.
 *
 * @since 8.34.0
 */
export const ToggleCard = forwardRef<HTMLDivElement, ToggleCardPropsInterface>(
  (props: ToggleCardPropsInterface, forwardedRef) => {
    const { body, children, dividers = true, footer, header, ...rest } = props;

    return (
      <ToggleCard.Root ref={forwardedRef} {...rest}>
        {header ? <ToggleCard.Header divider={dividers}>{header}</ToggleCard.Header> : null}
        {body ? <ToggleCard.Body>{body}</ToggleCard.Body> : null}
        {children}
        {footer ? <ToggleCard.Footer divider={dividers}>{footer}</ToggleCard.Footer> : null}
      </ToggleCard.Root>
    );
  },
) as ForwardRefExoticComponent<ToggleCardPropsInterface> & {
  Root: typeof ToggleCardRoot;
  RootTrigger: typeof ToggleCardRootTrigger;
  RootContent: typeof ToggleCardRootContent;
  Header: typeof ToggleCardHeader;
  Body: typeof ToggleCardBody;
  Footer: typeof ToggleCardFooter;
};

ToggleCard.displayName = 'ToggleCard';

ToggleCard.Root = ToggleCardRoot;
ToggleCard.RootTrigger = ToggleCardRootTrigger;
ToggleCard.RootContent = ToggleCardRootContent;
ToggleCard.Header = ToggleCardHeader;
ToggleCard.Body = ToggleCardBody;
ToggleCard.Footer = ToggleCardFooter;
